/**
 * Build-4: platform API outage recovery — health polling + overlay state.
 * Used by api.js (auto-retry after recovery), App.vue (Reconnecting overlay),
 * and auth pages (Login / Forgot / Reset / Change password).
 *
 * Step 4C: assessed deploy ETA from /deploy-eta.json (written by deploy-pilot.sh).
 * On poll timeout: keep overlay up (no silent clear); keep polling; offer Refresh.
 */

const HEALTH_POLL_MS = 2000;
// Long enough for stuck cutover / recovery; Build-6 build itself is unlocked (no overlay).
const HEALTH_MAX_ATTEMPTS = 300;

// Build-6: maintenance brackets cutover only; deploy-pilot.sh writes ETA_MINUTES_CUTOVER=3.
const DEFAULT_ETA_MINUTES = 3;

export const OUTAGE_USER_MESSAGE =
  'AccessIQ is updating. This should take about 3 minutes. Please wait and try again.';

let outageActive = false;
let outageTimedOut = false;
let etaMinutes = DEFAULT_ETA_MINUTES;
let recoveryPromise = null;
const listeners = new Set();

function getHealthUrl() {
  const base = process.env.VUE_APP_API_URL || 'http://localhost:8000/api';
  return `${base.replace(/\/$/, '')}/health/`;
}

function getDeployEtaUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/deploy-eta.json`;
  }
  return '/deploy-eta.json';
}

function outageMessageForMinutes(minutes) {
  const n = Number(minutes);
  const safe = Number.isFinite(n) && n > 0 ? Math.round(n) : DEFAULT_ETA_MINUTES;
  return `AccessIQ is updating. This should take about ${safe} minute${safe === 1 ? '' : 's'}. Please wait and try again.`;
}

function notifyListeners() {
  listeners.forEach((fn) => {
    fn({
      active: outageActive,
      timedOut: outageTimedOut,
      etaMinutes,
      message: outageMessageForMinutes(etaMinutes),
    });
  });
}

function setOutageActive(active) {
  if (outageActive === active) return;
  outageActive = active;
  if (!active) {
    outageTimedOut = false;
  }
  notifyListeners();
}

function setOutageTimedOut(timedOut) {
  if (outageTimedOut === timedOut) return;
  outageTimedOut = timedOut;
  notifyListeners();
}

/**
 * Read assessed minutes written by deploy-pilot.sh at maintenance start.
 * Falls back to 3 (Build-6 cutover) if missing or unreadable.
 */
export async function fetchDeployEtaMinutes() {
  try {
    const response = await fetch(getDeployEtaUrl(), {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!response.ok) {
      return DEFAULT_ETA_MINUTES;
    }
    const data = await response.json();
    const minutes = Number(data?.eta_minutes);
    if (Number.isFinite(minutes) && minutes > 0) {
      return Math.round(minutes);
    }
  } catch {
    // ignore — use default
  }
  return DEFAULT_ETA_MINUTES;
}

export function isServerOutageError(error) {
  if (!error) return false;
  if (!error.response) {
    return true;
  }
  const status = error.response.status;
  return status >= 502 && status <= 504;
}

export function subscribeOutageState(listener) {
  listeners.add(listener);
  listener({
    active: outageActive,
    timedOut: outageTimedOut,
    etaMinutes,
    message: outageMessageForMinutes(etaMinutes),
  });
  return () => listeners.delete(listener);
}

export function isOutageActive() {
  return outageActive;
}

export function isOutageTimedOut() {
  return outageTimedOut;
}

export function getOutageEtaMinutes() {
  return etaMinutes;
}

export function getOutageUserMessage() {
  return outageMessageForMinutes(etaMinutes);
}

async function pingHealth() {
  try {
    const response = await fetch(getHealthUrl(), {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Block until /api/health/ returns OK. Shows reconnect overlay while waiting.
 * Concurrent callers share one recovery poll (same pattern as token refresh).
 *
 * Note: on pilot, host nginx returns 503 for app/API routes while the
 * maintenance flag is set. /deploy-eta.json stays available (4C). Health only
 * succeeds after maintenance clears.
 *
 * On max attempts: keep overlay up (4C — no silent clear), mark timedOut,
 * keep polling until health returns or the user refreshes.
 */
export async function waitForBackendHealth() {
  if (await pingHealth()) {
    setOutageActive(false);
    return;
  }

  if (!recoveryPromise) {
    etaMinutes = await fetchDeployEtaMinutes();
    setOutageActive(true);
    recoveryPromise = (async () => {
      let attempt = 0;
      let healthy = false;
      while (!healthy) {
        if (await pingHealth()) {
          healthy = true;
          setOutageActive(false);
          return;
        }
        attempt += 1;
        if (attempt >= HEALTH_MAX_ATTEMPTS) {
          setOutageTimedOut(true);
        }
        await new Promise((resolve) => {
          setTimeout(resolve, HEALTH_POLL_MS);
        });
      }
    })().finally(() => {
      recoveryPromise = null;
    });
  }

  return recoveryPromise;
}

/**
 * Run an async auth action; on deploy outage, wait for health then retry once.
 * Works with api.js interceptor (which may already have retried) as a second line of defense.
 */
export async function runWithOutageRetry(asyncFn) {
  try {
    return await asyncFn();
  } catch (error) {
    if (!isServerOutageError(error) && !isOutageActive()) {
      throw error;
    }
    await waitForBackendHealth();
    return asyncFn();
  }
}

export function authErrorFromOutage(error, fallbackMessage) {
  if (isServerOutageError(error) || isOutageActive()) {
    return { isOutage: true, message: getOutageUserMessage() };
  }
  if (error?.response?.status === 401) {
    return {
      isOutage: false,
      message: error.response.data?.detail || 'Failed to login. Please check your credentials.',
    };
  }
  if (error?.response?.data?.detail) {
    return { isOutage: false, message: error.response.data.detail };
  }
  if (error?.response?.data?.error) {
    return { isOutage: false, message: error.response.data.error };
  }
  if (error?.message) {
    return { isOutage: false, message: error.message };
  }
  return { isOutage: false, message: fallbackMessage };
}

export default {
  OUTAGE_USER_MESSAGE,
  getOutageUserMessage,
  fetchDeployEtaMinutes,
  isServerOutageError,
  subscribeOutageState,
  isOutageActive,
  isOutageTimedOut,
  getOutageEtaMinutes,
  waitForBackendHealth,
  runWithOutageRetry,
  authErrorFromOutage,
};
